// Standortbericht (Phase E): Entgeltreihe mit Diagramm, Belege, Einschätzungsreife; für GQL (oder nach Freischaltung)
// Verjährungsfenster und Rückforderungsschätzung mit einstellbaren Annahmen. Druckfähig; Markdown-Export für den Memo-Anhang.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { DbPortalStandortAnalyse, DbPortalVerjaehrung } from '@gql/shared-types';
import { api, API_BASIS, ApiFehler } from '../api/client.js';
import { useAuth } from '../auth/AuthKontext.js';
import { AnnahmenFormular } from '../components/AnnahmenFormular.js';
import { achsenMaximum, annahmenAbfrage, diagrammAbschnitte, diagrammPfad, monatText, QUELLE_TEXT, REIFE_TEXT, standardAnnahmen, type Annahmen } from '../daten/analyse.js';
import { datumText, euro, zeitpunkt } from '../daten/lieferungen.js';

const ART_TEXT: Record<string, string> = { vertrag: 'Vertrag', rechnung: 'Rechnung', leistungsbeschreibung: 'Leistungsbeschreibung', agb: 'AGB', entgeltaenderung: 'Entgeltänderung', sonstiges: 'Sonstiges' };

export function StandortBericht() {
  const { id } = useParams();
  const { zustand } = useAuth();
  const admin = zustand.status === 'bereit' && zustand.ich.nutzer?.rolle === 'gql_admin';
  const [annahmen, setAnnahmen] = useState<Annahmen>(standardAnnahmen);
  const [a, setA] = useState<DbPortalStandortAnalyse | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const abfrage = useMemo(() => annahmenAbfrage(annahmen), [annahmen]);
  const erstesLaden = useRef(true);

  useEffect(() => {
    if (annahmen.kenntnis === 'datum' && !annahmen.kenntnisDatum) return; // erst mit Datum abfragen
    let aktiv = true;
    // Änderungen im Annahmen-Formular erst nach einer kurzen Pause abfragen und veraltete Anfragen abbrechen – sonst
    // löst jeder Tastendruck eine Berechnung aus und erschöpft die Ratenbegrenzung (Review E, P2-6)
    const abbruch = new AbortController();
    const verzoegerung = erstesLaden.current ? 0 : 400;
    erstesLaden.current = false;
    const zeitgeber = setTimeout(() => {
      setLaedt(true);
      api<DbPortalStandortAnalyse>(`/standorte/${id}/analyse${abfrage ? `?${abfrage}` : ''}`, { signal: abbruch.signal })
        .then((d) => {
          if (!aktiv) return;
          setA(d);
          setFehler(null);
        })
        .catch((f: ApiFehler) => aktiv && setFehler(f.message))
        .finally(() => aktiv && setLaedt(false));
    }, verzoegerung);
    return () => {
      aktiv = false;
      clearTimeout(zeitgeber);
      abbruch.abort();
    };
  }, [id, abfrage, annahmen.kenntnis, annahmen.kenntnisDatum]);

  if (fehler && !a) {
    return (
      <div className="seite">
        <p className="brotkrumen">
          <Link to={`/standorte/${id}`}>← Zum Standort</Link>
        </p>
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      </div>
    );
  }
  if (!a) {
    return (
      <div className="seite" role="status">
        <span className="spinner" aria-hidden="true" /> Bericht wird berechnet …
      </div>
    );
  }
  const vollstaendig = a.verjaehrung !== null;
  const monateBelegt = a.monate.filter((m) => m.cent !== null).length;

  return (
    <article className="seite bericht">
      <p className="brotkrumen nicht-drucken">
        <Link to={`/standorte/${a.standort.id}`}>← Zum Standort</Link>
      </p>
      <header className="titelzeile">
        <div>
          <h1>Standortbericht: {a.standort.name}</h1>
          <p className="gebiet-unterzeile">
            {[a.standort.externeId && `ID ${a.standort.externeId}`, a.standort.ort, a.standort.gebietName ? `Gebiet ${a.standort.gebietName}` : 'Gebiet noch offen', a.standort.skalaWert !== null && `Vergleichbarkeit ${a.standort.skalaWert}/5`]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="knopfreihe nicht-drucken">
          <a className="knopf-sekundaer knopf-auto" href={`${API_BASIS}/standorte/${a.standort.id}/bericht.md${abfrage ? `?${abfrage}` : ''}`} download>
            Als Markdown herunterladen
          </a>
          <button type="button" className="knopf" onClick={() => window.print()}>
            Drucken
          </button>
        </div>
      </header>
      <p className="hinweis-klein">
        Automatische Auswertung und Rechenhilfe mit den unten genannten Annahmen – keine Rechtsauskunft; maßgeblich sind die Originalunterlagen. Stand {zeitpunkt(a.stand)}
        {laedt && <span className="zelle-hinweis"> · wird aktualisiert …</span>}
      </p>
      {(admin || vollstaendig) && (
        <div className="nicht-drucken">
          <AnnahmenFormular wert={annahmen} onChange={setAnnahmen} mitVergleichswert={vollstaendig} />
          {fehler && (
            <p className="fehlertext" role="alert">
              {fehler}
            </p>
          )}
        </div>
      )}

      <div className="kennzahlen">
        <div className="kennzahl">
          <span className="kennzahl-label">Einschätzungsreife</span>
          <span className={`kennzahl-wert reife reife-${a.reife.stufe}`}>{REIFE_TEXT[a.reife.stufe]}</span>
          <span className="zelle-hinweis">
            Vertrag {a.reife.vertrag ? 'liegt vor' : 'fehlt'} · Rechnungen {a.reife.rechnungsjahre} von {a.reife.kernjahre} Kernjahren · Entgelt für {a.reife.monateBelegt} von {a.reife.kernmonate} Kernmonaten
          </span>
        </div>
        <div className="kennzahl">
          <span className="kennzahl-label">Kennwert Monatsentgelt netto</span>
          <span className="kennzahl-wert">{a.kennwertCent !== null ? euro(a.kennwertCent) : '–'}</span>
          <span className="zelle-hinweis">
            Median der sicheren Monate {a.kern.von}–{a.kern.bis} · {monateBelegt} Monate belegt
          </span>
        </div>
        {a.verjaehrung && <VerjaehrungKennzahl v={a.verjaehrung} />}
      </div>
      {a.reife.luecken.length > 0 && (
        <ul className="luecken">
          {a.reife.luecken.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      )}

      <section aria-labelledby="h-reihe">
        <h2 id="h-reihe">Entgeltreihe (Monatsentgelt netto)</h2>
        {monateBelegt === 0 ? (
          <p className="leer">Noch keine Unterlage mit auswertbarem Entgelt.</p>
        ) : (
          <>
            <EntgeltDiagramm a={a} />
            <div className="tabelle-rahmen">
              <table className="tabelle tabelle-klein tabelle-karten">
                <caption className="sr-only">Entgeltreihe je Jahr</caption>
                <thead>
                  <tr>
                    <th scope="col">Jahr</th>
                    <th scope="col">Monate belegt</th>
                    <th scope="col">davon sicher</th>
                    <th scope="col">niedrigstes</th>
                    <th scope="col">Median</th>
                    <th scope="col">höchstes</th>
                    <th scope="col">Summe der belegten Monate</th>
                    <th scope="col">Quellen</th>
                  </tr>
                </thead>
                <tbody>
                  {a.jahre.map((j) => (
                    <tr key={j.jahr} className={j.jahr >= a.kern.von && j.jahr <= a.kern.bis ? 'ist-kern' : ''}>
                      <td data-label="Jahr">{j.jahr}</td>
                      <td data-label="Monate belegt" className="zahl">
                        {j.monateBelegt}
                      </td>
                      <td data-label="davon sicher" className="zahl">
                        {j.monateSicher}
                      </td>
                      <td data-label="niedrigstes" className="zahl">
                        {j.minCent !== null ? euro(j.minCent) : '–'}
                      </td>
                      <td data-label="Median" className="zahl">
                        {j.medianCent !== null ? euro(j.medianCent) : '–'}
                      </td>
                      <td data-label="höchstes" className="zahl">
                        {j.maxCent !== null ? euro(j.maxCent) : '–'}
                      </td>
                      <td data-label="Summe" className="zahl">
                        {j.summeCent !== null ? euro(j.summeCent) : '–'}
                      </td>
                      <td data-label="Quellen">{j.quellen.map((q) => QUELLE_TEXT[q]).join(', ') || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section aria-labelledby="h-belege">
        <h2 id="h-belege">Belege</h2>
        {a.belege.length === 0 ? (
          <p className="leer">Keine Unterlage mit lesbarem Entgelt.</p>
        ) : (
          <div className="tabelle-rahmen">
            <table className="tabelle tabelle-klein tabelle-karten">
              <caption className="sr-only">Belege der Entgeltreihe</caption>
              <thead>
                <tr>
                  <th scope="col">Unterlage</th>
                  <th scope="col">Monatsentgelt</th>
                  <th scope="col">gilt ab</th>
                  <th scope="col">bis</th>
                  <th scope="col">Herleitung</th>
                  <th scope="col">Stand</th>
                </tr>
              </thead>
              <tbody>
                {a.belege.map((b, i) => (
                  <tr key={`${b.dokumentId}-${i}`}>
                    <td data-label="Unterlage">
                      {b.dateiname}
                      <span className="zelle-hinweis">{ART_TEXT[b.art] ?? b.art}</span>
                    </td>
                    <td data-label="Monatsentgelt" className="zahl">
                      {euro(b.monatCent)}
                    </td>
                    <td data-label="gilt ab">{monatText(b.von)}</td>
                    <td data-label="bis">{b.bis ? monatText(b.bis) : 'nächste Änderung'}</td>
                    <td data-label="Herleitung">{b.herleitung}</td>
                    <td data-label="Stand">{[b.sicher ? 'gesichert' : 'bitte prüfen', b.uebernommen ? 'übernommen' : 'in Prüfung'].join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {a.verjaehrung && (
        <section aria-labelledby="h-verjaehrung">
          <h2 id="h-verjaehrung">Verjährungsfenster (Rechenhilfe)</h2>
          <p className="hinweis-klein">
            Kartellschadensersatz (§ 33a GWB):{' '}
            {a.verjaehrung.gwb.offenAb ? `nach den Annahmen nicht verjährt für Zahlungen ab ${monatText(a.verjaehrung.gwb.offenAb)}` : 'nach den Annahmen für alle erfassten Monate verjährt'}
            {a.verjaehrung.gwb.naechsterAblauf && `; nächster Fristablauf ${datumText(a.verjaehrung.gwb.naechsterAblauf)}`}. BGB-Ansprüche (§ 315 Abs. 3, § 812 BGB):{' '}
            {a.verjaehrung.bgb.offenAb ? `nicht verjährt für Zahlungen ab ${monatText(a.verjaehrung.bgb.offenAb)}` : 'für alle erfassten Monate verjährt'}
            {a.verjaehrung.bgb.naechsterAblauf && `; nächster Fristablauf ${datumText(a.verjaehrung.bgb.naechsterAblauf)}`}.
          </p>
          <div className="tabelle-rahmen">
            <table className="tabelle tabelle-klein tabelle-karten verjaehrung">
              <caption className="sr-only">Verjährung je Zahlungsjahr</caption>
              <thead>
                <tr>
                  <th scope="col">Zahlungsjahr</th>
                  <th scope="col">BGB (laufend / offen / verjährt)</th>
                  <th scope="col">Ablauf BGB (Dezember)</th>
                  <th scope="col">GWB (laufend / offen / verjährt)</th>
                  <th scope="col">Ablauf GWB (Dezember)</th>
                </tr>
              </thead>
              <tbody>
                {a.verjaehrung.jahre.map((j) => (
                  <tr key={j.jahr} className={j.gwb.verjaehrt === j.monate && j.bgb.verjaehrt === j.monate ? 'ist-verjaehrt' : ''}>
                    <td data-label="Zahlungsjahr">{j.jahr}</td>
                    <td data-label="BGB">{`${j.bgb.laeuft} / ${j.bgb.offen} / ${j.bgb.verjaehrt}`}</td>
                    <td data-label="Ablauf BGB">{j.bgb.ablauf ? datumText(j.bgb.ablauf) : '–'}</td>
                    <td data-label="GWB">{`${j.gwb.laeuft} / ${j.gwb.offen} / ${j.gwb.verjaehrt}`}</td>
                    <td data-label="Ablauf GWB">{j.gwb.ablauf ? datumText(j.gwb.ablauf) : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {vollstaendig && (
        <section aria-labelledby="h-schaetzung">
          <h2 id="h-schaetzung">Rückforderungsschätzung</h2>
          {!a.schaetzungen?.length ? (
            <p className="hinweis-klein">Bitte oben bis zu drei Vergleichswerte (Monatsentgelt netto) eingeben – die Überzahlung ist die Differenz je Monat, nur nicht verjährte Monate zählen.</p>
          ) : (
            a.schaetzungen.map((s, n) => (
              <div key={s.vergleichswertCent} className="szenario">
                <h3>
                  Szenario {n + 1}: Vergleichswert {euro(s.vergleichswertCent)} je Monat
                </h3>
                <div className="tabelle-rahmen">
                  <table className="tabelle tabelle-klein tabelle-karten">
                    <caption className="sr-only">Rückforderungsschätzung, Vergleichswert {euro(s.vergleichswertCent)}</caption>
                    <thead>
                      <tr>
                        <th scope="col">Anspruch</th>
                        <th scope="col">Monate</th>
                        <th scope="col">Zeitraum</th>
                        <th scope="col">Überzahlung</th>
                        <th scope="col">Zinsen bis Stichtag</th>
                        <th scope="col">Summe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(
                        [
                          ['Kartellschadensersatz (§ 33a GWB), nicht verjährt', s.gwb],
                          ['BGB (§ 315 Abs. 3 / § 812), nicht verjährt', s.bgb],
                          ['alle belegten Monate (Obergrenze ohne Verjährung)', s.gesamt],
                        ] as const
                      ).map(([name, b]) => (
                        <tr key={name}>
                          <td data-label="Anspruch">{name}</td>
                          <td data-label="Monate" className="zahl">
                            {b.monate}
                            {b.monateOhneWert > 0 && <span className="zelle-hinweis">{b.monateOhneWert} ohne Wert</span>}
                          </td>
                          <td data-label="Zeitraum">{b.monate ? `${monatText(b.vonMonat)} – ${monatText(b.bisMonat)}` : '–'}</td>
                          <td data-label="Überzahlung" className="zahl">
                            {euro(b.hauptforderungCent)}
                          </td>
                          <td data-label="Zinsen" className="zahl">
                            {euro(b.zinsenCent)}
                          </td>
                          <td data-label="Summe" className="zahl">
                            <strong>{euro(b.hauptforderungCent + b.zinsenCent)}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {n === a.schaetzungen!.length - 1 && (
                  <p className="zelle-hinweis">
                    Zinsen: {s.zinsen.methode}; Basiszinssätze: {s.zinsen.quelle} (abgerufen {datumText(s.zinsen.abgerufen)})
                    {s.zinsen.ausserhalbTabelle && ' – Teile des Zeitraums liegen außerhalb der Tabelle, Satz fortgeschrieben'}. Überzahlung = Monatsentgelt − Vergleichswert, nur positive Differenzen; Monate ohne Wert gehen mit null ein.
                  </p>
                )}
              </div>
            ))
          )}
        </section>
      )}

      {a.verjaehrung && (
        <section aria-labelledby="h-annahmen">
          <h2 id="h-annahmen">Annahmen</h2>
          <ul className="annahmen-liste">
            {a.verjaehrung.annahmen.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </section>
      )}
      {a.hinweise.length > 0 && (
        <section aria-labelledby="h-hinweise">
          <h2 id="h-hinweise">Hinweise</h2>
          <ul className="annahmen-liste">
            {a.hinweise.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

function VerjaehrungKennzahl({ v }: { v: DbPortalVerjaehrung }) {
  return (
    <div className="kennzahl">
      <span className="kennzahl-label">Kartellschadensersatz nicht verjährt (Rechenhilfe)</span>
      <span className="kennzahl-wert">{v.gwb.offenAb ? `ab ${monatText(v.gwb.offenAb)}` : 'kein Monat'}</span>
      <span className="zelle-hinweis">
        {v.gwb.monateLaufend} Monate laufend · {v.gwb.monateOffen} noch nicht angelaufen · {v.gwb.monateVerjaehrt} verjährt
        {v.gwb.naechsterAblauf && ` · nächster Ablauf ${datumText(v.gwb.naechsterAblauf)}`}
      </span>
    </div>
  );
}

const B = 1000;
const H = 240;
const L = 70;
const R = 16;
const O = 14;
const U = 34;

function EntgeltDiagramm({ a }: { a: DbPortalStandortAnalyse }) {
  const monate = a.monate;
  const maxCent = achsenMaximum(Math.max(...monate.map((m) => m.cent ?? 0)));
  const x = (i: number) => L + (i / Math.max(1, monate.length - 1)) * (B - L - R);
  const y = (cent: number) => O + (1 - cent / maxCent) * (H - O - U);
  const abschnitte = diagrammAbschnitte(monate);
  const jahre = [...new Set(monate.map((m) => Number(m.monat.slice(0, 4))))];
  const schritt = jahre.length > 14 ? 2 : 1;
  const raster = [0.25, 0.5, 0.75, 1].map((f) => Math.round(maxCent * f));
  const kernVon = monate.findIndex((m) => m.monat.startsWith(String(a.kern.von)));
  const kernBis = monate.map((m) => m.monat.startsWith(String(a.kern.bis))).lastIndexOf(true);
  const beschreibung = a.jahre
    .filter((j) => j.medianCent !== null)
    .map((j) => `${j.jahr}: ${euro(j.medianCent!)}`)
    .join(', ');
  return (
    <figure className="diagramm">
      <svg viewBox={`0 0 ${B} ${H}`} role="img" aria-label={`Monatsentgelt netto im Zeitverlauf (Median je Jahr): ${beschreibung}`}>
        {kernVon >= 0 && kernBis >= 0 && <rect x={x(kernVon)} y={O} width={x(kernBis) - x(kernVon)} height={H - O - U} fill="#f5f5f4" />}
        {raster.map((c) => (
          <g key={c}>
            <line x1={L} x2={B - R} y1={y(c)} y2={y(c)} stroke="#e7e5e4" />
            <text x={L - 6} y={y(c) + 4} textAnchor="end" className="diagramm-achse">
              {euro(c).replace(',00', '')}
            </text>
          </g>
        ))}
        <line x1={L} x2={B - R} y1={y(0)} y2={y(0)} stroke="#a8a29e" />
        {jahre.map((j, n) => {
          const i = monate.findIndex((m) => m.monat.startsWith(String(j)));
          return (
            <g key={j}>
              <line x1={x(i)} x2={x(i)} y1={y(0)} y2={y(0) + 5} stroke="#a8a29e" />
              {n % schritt === 0 && (
                <text x={x(i)} y={y(0) + 20} textAnchor="middle" className="diagramm-achse">
                  {j}
                </text>
              )}
            </g>
          );
        })}
        {abschnitte.map((s, n) => (
          <path key={n} d={diagrammPfad(s, x, y)} fill="none" stroke="#0f766e" strokeWidth={s.sicher ? 2.5 : 2} strokeDasharray={s.sicher ? undefined : '6 5'} strokeOpacity={s.sicher ? 1 : 0.6} strokeLinejoin="round" />
        ))}
      </svg>
      <figcaption className="zelle-hinweis">Durchgezogen: gesicherte Werte; gestrichelt: fortgeschrieben oder ohne bestätigte Fundstelle. Grau hinterlegt: Kernzeitraum {a.kern.von}–{a.kern.bis}.</figcaption>
    </figure>
  );
}
